import { useTranslation } from 'react-i18next';
import { paymentActions, evidencePayload } from '../model/payment-execution.model.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapTradingAccountsResponseToDto } from '../../system-trading-accounts/dto/system-trading-accounts.dto.js';
import { mapTradingPlatformAliasesResponseToDto } from '../../system-trading-platforms/dto/system-trading-platforms.dto.js';
import { mapTradingServersResponseToDto } from '../../system-trading-servers/dto/system-trading-servers.dto.js';
import { mapTransactionDetailsResponseToDto } from '../dto/transaction-details.dto.js';
import { buildTransactionDetailsModel } from '../model/transaction-details.model.js';
import { isFinalTransactionStatus } from '../ui/transaction-status-badge.js';

export function useTransactionDetails() {
  const { i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const { token, hasPermission } = useAuth();
  const savingRef = useRef(false);
  const { id } = useParams();
  const currentRouteId = useRef(id);
  currentRouteId.current = id;
  const navigate = useNavigate();
  const [transactionData, setTransactionData] = useState(null);
  const transaction = useMemo(() => transactionData ? buildTransactionDetailsModel(
    mapTransactionDetailsResponseToDto(transactionData.response, locale),
    { ...transactionData.options, locale },
  ) : null, [transactionData, locale]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [manualEvidenceForm, setManualEvidenceForm] = useState({sourceReference:'',amount:'',outcome:'settled'});
  const allowedPaymentActions = paymentActions(transaction?.paymentExecution, hasPermission);
  const [manualOwnTransferForm, setManualOwnTransferForm] = useState({
    sourcePlatformOrderId: '',
    targetPlatformOrderId: '',
    reason: '',
  });
  const isActionDisabled = isFinalTransactionStatus(transaction?.status);
  const isOwnTransferPending = String(transaction?.type || '').trim().toLowerCase() === 'own_transfer'
    && String(transaction?.status || '').trim().toLowerCase() === 'pending';

  const resolveAccountName = useCallback((dto, tradingAccounts) => {
    const normalizedServerId = String(dto?.tradingServerId || '').trim();
    const normalizedPlatform = String(dto?.platform || '').trim();
    const normalizedType = String(dto?.accountType || '').trim();

    if (!normalizedType) {
      return '';
    }

    const rows = Array.isArray(tradingAccounts) ? tradingAccounts : [];
    const exactServerMatch = rows.find((item) =>
      String(item?.tradingServerId || '').trim() === normalizedServerId
      && String(item?.type || '').trim() === normalizedType,
    );
    if (exactServerMatch?.name) {
      return exactServerMatch.name;
    }

    const platformMatch = rows.find((item) =>
      String(item?.platform || '').trim() === normalizedPlatform
      && String(item?.type || '').trim() === normalizedType,
    );
    if (platformMatch?.name) {
      return platformMatch.name;
    }

    const typeMatch = rows.find((item) => String(item?.type || '').trim() === normalizedType);
    return typeMatch?.name || '';
  }, []);

  const loadTransaction = useCallback(async () => {
    if (currentRouteId.current !== id) return;
    if (!id) {
      setTransactionData(null);
      setError('Transaction id is required');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const [transactionResponse, tradingServersResponse, tradingPlatformsResponse, tradingAccountsResponse] = await Promise.all([
        adminApi.getTransaction(token, id),
        adminApi.getTradingServers(token).catch(() => null),
        adminApi.getTradingPlatformAliases(token).catch(() => null),
        adminApi.getTradingAccounts(token).catch(() => null),
      ]);
      const tradingServerNameById = new Map(
        mapTradingServersResponseToDto(tradingServersResponse).map((item) => [item.id, item.name || item.id]),
      );
      const platformLabelByValue = new Map(
        mapTradingPlatformAliasesResponseToDto(tradingPlatformsResponse).map((item) => [
          item.platform,
          item.alias || item.label || item.platform,
        ]),
      );
      const tradingAccounts = mapTradingAccountsResponseToDto(tradingAccountsResponse);
      const dto = mapTransactionDetailsResponseToDto(transactionResponse);
      const modelOptions = {
        tradingServerNameById,
        platformLabelByValue,
        accountNameResolver: (value) => resolveAccountName(value, tradingAccounts),
      };
      if (currentRouteId.current !== id) return;
      setTransactionData({ response: transactionResponse, options: modelOptions });
      setManualOwnTransferForm({
        sourcePlatformOrderId: dto?.sourceAccountOrderId === '-' ? '' : dto?.sourceAccountOrderId || '',
        targetPlatformOrderId: dto?.targetAccountOrderId === '-' ? '' : dto?.targetAccountOrderId || '',
        reason: '',
      });
    } catch (requestError) {
      if (currentRouteId.current !== id) return;
      setTransactionData(null);
      setError(requestError.message || 'Failed to load transaction details');
    } finally {
      if (currentRouteId.current === id) setIsLoading(false);
    }
  }, [id, resolveAccountName, token]);

  useEffect(() => {
    setManualEvidenceForm({sourceReference:'',amount:'',outcome:'settled'});
  }, [id]);

  useEffect(() => {
    loadTransaction();
  }, [loadTransaction]);

  const navigateBack = useCallback(() => {
    navigate('/transactions');
  }, [navigate]);

  const runAction = useCallback(async (action) => {
    if (!id || transaction?.id !== id || savingRef.current || (transaction?.paymentExecution ? !paymentActions(transaction.paymentExecution, hasPermission).includes(action) : isFinalTransactionStatus(transaction?.status))) {
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      if (action === 'approve') {
        await adminApi.approveTransaction(token, id);
        if (currentRouteId.current !== id) return;
        setSuccess(transaction?.paymentExecution ? 'Approval recorded' : 'Transaction approved');
      } else if (action === 'decline') {
        await adminApi.declineTransaction(token, id);
        if (currentRouteId.current !== id) return;
        setSuccess(transaction?.paymentExecution ? 'Cancellation requested' : 'Transaction declined');
      } else if (action === 'refresh') {
        await adminApi.refreshPaymentExecution(token, id);
        if (currentRouteId.current !== id) return;
        setSuccess('Payment status refreshed');
      } else if (action === 'record_manual_evidence') {
        await adminApi.recordPaymentEvidence(token, id, evidencePayload(transaction.paymentExecution, manualEvidenceForm));
        if (currentRouteId.current !== id) return;
        setSuccess('Transfer evidence recorded');
        setManualEvidenceForm({sourceReference:'',amount:'',outcome:'settled'});
      } else {
        throw new Error('Unsupported transaction action');
      }

      await loadTransaction();
    } catch (requestError) {
      if (currentRouteId.current === id) setError(requestError.message || 'Failed to update transaction');
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [id, loadTransaction, token, transaction, hasPermission, manualEvidenceForm]);

  const approveTransaction = useCallback(async () => {
    await runAction('approve');
  }, [runAction]);

  const declineTransaction = useCallback(async () => {
    await runAction('decline');
  }, [runAction]);

  const processOwnTransferNow = useCallback(async () => {
    if (!id || !isOwnTransferPending) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.processOwnTransferNow(token, id);
      setSuccess('Internal transfer processed');
      await loadTransaction();
    } catch (requestError) {
      setError(requestError.message || 'Failed to process internal transfer');
    } finally {
      setIsSaving(false);
    }
  }, [id, isOwnTransferPending, loadTransaction, token]);

  const updateManualOwnTransferField = useCallback((field, value) => {
    setManualOwnTransferForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const completeOwnTransferManually = useCallback(async () => {
    if (!id || !isOwnTransferPending) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.completeOwnTransferManually(token, id, manualOwnTransferForm);
      setSuccess('Internal transfer completed');
      await loadTransaction();
    } catch (requestError) {
      setError(requestError.message || 'Failed to complete internal transfer');
    } finally {
      setIsSaving(false);
    }
  }, [id, isOwnTransferPending, loadTransaction, manualOwnTransferForm, token]);

  return {
    transaction,
    allowedPaymentActions,
    refreshPaymentExecution: () => runAction('refresh'),
    recordPaymentEvidence: () => runAction('record_manual_evidence'),
    manualEvidenceForm,
    updateManualEvidenceField: (field,value) => setManualEvidenceForm(current=>({...current,[field]:value})),
    isLoading,
    isSaving,
    error,
    success,
    isActionDisabled,
    navigateBack,
    approveTransaction,
    declineTransaction,
    canManageOwnTransfer: isOwnTransferPending,
    manualOwnTransferForm,
    updateManualOwnTransferField,
    processOwnTransferNow,
    completeOwnTransferManually,
  };
}
